export const fetchContent = async (language) => {
  const debug = false;
  if (debug) console.log('🚀 fetchContent called with language:', language);

  const apiUrl = process.env.REACT_APP_API_URL;
  const disableCaching = process.env.REACT_APP_DISABLE_CONTENT_CACHING === 'true' ?? false;
  const cacheExpiryTime = 3600000;

  if (debug) console.log('⚙️ Configuration:', { 
    apiUrl, 
    disableCaching, 
    cacheExpiryTime: cacheExpiryTime / 1000 / 60 + ' minutes' 
  });

  const locale = language === 'en' ? 'en' : 'nl';
  // always put https:// in the url because strapi expects it.
  const hostname = window.location.hostname;
  const urlParams = new URLSearchParams(window.location.search);
  const site = 'https://' + (
    (hostname.includes('rubencruijsen') || urlParams.get('rubencruijsen')) ? 'rubencruijsen.nl' : 'rupswebsites.nl'
  ) + (locale === 'nl' ? '' : '/en/');
  // const site = 'https://rups.cloud';

  if (debug) console.log('🌐 Site and locale determined:', { site, locale });

  try {
    const cachedData = localStorage.getItem(`content-${site}-${locale}`);
    const cachedTimestamp = parseInt(localStorage.getItem(`content-timestamp-${site}-${locale}`), 10);
    const currentTime = new Date().getTime();

    if (debug) console.log('💾 Cache check:', {
      hasCachedData: !!cachedData,
      cachedTimestamp: cachedTimestamp ? new Date(cachedTimestamp).toISOString() : 'none',
      currentTime: new Date(currentTime).toISOString(),
      timeDiff: cachedTimestamp ? (currentTime - cachedTimestamp) / 1000 / 60 + ' minutes' : 'N/A',
      cacheValid: !disableCaching && cachedData && cachedTimestamp && (currentTime - cachedTimestamp < cacheExpiryTime)
    });
    
    if (!disableCaching && (cachedData && (cachedTimestamp && (currentTime - cachedTimestamp < cacheExpiryTime)))) {
      if (debug) console.log('✅ Using cached data');
      return JSON.parse(cachedData);
    }

    if (debug) console.log('🌐 Fetching fresh data from API...');
    const toFetch = {
      sites: '[url]',
      portfolios: '[site][url]',
      services: '[sites][url]',
      skills: '[sites][url]',
      experiences: '[sites][url]',
      projects: '[sites][url]',
      seos: '[site][url]'
    };
    
    if (debug) console.log('📝 API endpoints to fetch:', Object.keys(toFetch));

    const fetchedData = await Promise.all(Object.keys(toFetch).map(path => {
      const index = toFetch[path]
      const filter = "?filters" + index + "[$eq]=" + site + '&populate=*' + (locale ? `&locale=${locale}` : '');
      const url = `${apiUrl}/api/${path}${filter}`;
      if (debug) console.log(`🔗 Fetching ${path}:`, url);
      
      try {
        return fetch(url).then((response) => {
          if (debug) console.log(`📥 Response for ${path}:`, {
            status: response.status,
            statusText: response.statusText,
            ok: response.ok 
          });
          return response.json() ?? [];
        });
      }catch(error){
        console.error(`❌ Error fetching ${path}:`, error);
        return [];
      }
    }));

    const content = Object.keys(toFetch).reduce((acc, key, index) => {
      acc[key] = fetchedData[index];
      if (debug) console.log(`📊 Content for ${key}:`, {
        hasData: !!fetchedData[index],
        dataLength: fetchedData[index]?.data?.length || 0,
        structure: fetchedData[index] ? Object.keys(fetchedData[index]) : []
      });
      return acc;
    }, {});

    if (debug) console.log('🏗️ Building data structure...');

    const data = {
      'manifest': `${apiUrl}/api/manifest.json?filters[site][url][$eq]=${site}&populate=*&locale=${locale}`,
      'portfolio': content.portfolios.data[0],
      'items': [],
      'seo_title': content.seos.data[0]?.title || 'Default Title',
      'seo_description': content.seos.data[0]?.description || 'Default description',
    };

    if (debug) console.log('🎯 Initial data structure:', {
      hasPortfolio: !!data.portfolio,
      portfolioKeys: data.portfolio ? Object.keys(data.portfolio) : [],
      seoTitle: data.seo_title,
      seoDescription: data.seo_description
    });
    
    Object.keys(toFetch).forEach((key) => {
      const titleKey = key + "_title";
      const descriptionKey = key + "_description";
      const hasTitle = data.portfolio && data.portfolio[titleKey];

      if (debug) console.log(`🔍 Processing ${key}:`, {
        titleKey,
        hasTitle,
        titleValue: data.portfolio?.[titleKey],
        itemsCount: content[key]?.data?.length || 0
      });
      
      if (hasTitle) {
        data.items.push({
          'id': key,
          'title': data.portfolio[titleKey],
          'description': data.portfolio[descriptionKey],
          'items': content[key].data
        });
        if (debug) console.log(`✅ Added ${key} to items`);
      } else {
        if (debug) console.log(`⚠️ Skipped ${key} - no title found`);
      }
    });

    if (debug) console.log('📄 Setting document title:', data.seo_title);
    document.title = data.seo_title;
    // document.querySelector('meta[name="description"]').setAttribute('content', data.seo_description);

    if (debug) console.log('💾 Caching data...', {
      cacheKey: `content-${site}-${locale}`,
      timestampKey: `content-timestamp-${site}-${locale}`,
      dataSize: JSON.stringify(data).length + ' characters'
    });
    
    localStorage.setItem(`content-${site}-${locale}`, JSON.stringify(data));
    localStorage.setItem(`content-timestamp-${site}-${locale}`, currentTime.toString());

    if (debug) console.log('✅ Content fetch completed successfully:', {
      portfolioExists: !!data.portfolio,
      itemsCount: data.items.length,
      finalStructure: Object.keys(data)
    });
    
    return data;
  } catch (error) {
    // console.error('❌ Critical error in fetchContent:', error);
    // console.error('📊 Error details:', {
    //   message: error.message,
    //   stack: error.stack,
    //   name: error.name
    // });
    return null;
  }
};
